import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Download, Eye, DollarSign, TrendingUp, CreditCard, Users, FileText, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/admin/EmptyState";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Invoice {
  id: string;
  user_id: string;
  subscription_id: string | null;
  amount_cents: number;
  status: string;
  payment_method: string | null;
  paid_at: string | null;
  created_at: string;
  nfse_pdf_url: string | null;
  nfse_url: string | null;
  nfse_number: string | null;
  nfse_verification_code?: string | null;
  tiny_nfse_id: string | null;
  pagarme_charge_id: string | null;
  pagarme_invoice_id: string | null;
  nfse_issued_at?: string | null;
}

interface Profile {
  id: string;
  full_name: string | null;
  cpf: string | null;
  health_area: string | null;
}

interface InvoiceWithProfile extends Invoice {
  profile: Profile & {
    tiny_customer_id?: string | null;
  };
}

const Pagamentos = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<InvoiceWithProfile[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithProfile | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [syncingCustomer, setSyncingCustomer] = useState<string | null>(null);
  const [generatingNfse, setGeneratingNfse] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("invoices")
        .select(`
          *,
          profile:user_id (
            id,
            full_name,
            cpf,
            health_area,
            tiny_customer_id
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Transform data to match InvoiceWithProfile interface
      const transformedData = (data || []).map(invoice => ({
        ...invoice,
        profile: Array.isArray(invoice.profile) ? invoice.profile[0] : invoice.profile
      })) as InvoiceWithProfile[];

      setInvoices(transformedData);
    } catch (error) {
      console.error("Erro ao carregar pagamentos:", error);
      toast.error("Erro ao carregar pagamentos");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      paid: { label: "Pago", variant: "default" },
      pending: { label: "Pendente", variant: "secondary" },
      failed: { label: "Falhou", variant: "destructive" },
      refunded: { label: "Reembolsado", variant: "outline" },
    };

    const config = variants[status] || { label: status, variant: "outline" };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredInvoices = invoices.filter((invoice) => {
    const search = searchTerm.toLowerCase();
    return (
      invoice.profile?.full_name?.toLowerCase().includes(search) ||
      invoice.profile?.cpf?.toLowerCase().includes(search) ||
      invoice.nfse_number?.toLowerCase().includes(search) ||
      invoice.pagarme_charge_id?.toLowerCase().includes(search)
    );
  });

  // Calcular estatísticas
  const totalArrecadado = invoices
    .filter(i => i.status === 'paid')
    .reduce((sum, i) => sum + i.amount_cents, 0);

  const totalPagamentos = invoices.length;
  const taxaSucesso = totalPagamentos > 0
    ? ((invoices.filter(i => i.status === 'paid').length / totalPagamentos) * 100).toFixed(1)
    : '0';

  const handleViewInvoice = (invoice: InvoiceWithProfile) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  const handleSyncCustomer = async (invoice: InvoiceWithProfile) => {
    try {
      setSyncingCustomer(invoice.id);

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: {
          action: 'sync-customer',
          userId: invoice.user_id
        }
      });

      if (error) throw error;

      toast.success("Cliente sincronizado com sucesso no Tiny ERP");
      await loadInvoices(); // Reload to get updated tiny_customer_id
    } catch (error: any) {
      console.error("Erro ao sincronizar cliente:", error);
      toast.error(error.message || "Erro ao sincronizar cliente");
    } finally {
      setSyncingCustomer(null);
    }
  };

  const handleGenerateNfse = async (invoice: InvoiceWithProfile) => {
    try {
      setGeneratingNfse(invoice.id);
      toast.loading('Gerando NFS-e (sincronizando cliente se necessário)...', { id: 'generate-nfse' });

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: {
          action: 'complete-flow',
          invoiceId: invoice.id
        }
      });

      if (error) throw error;

      toast.success(data.message || "NFS-e gerada com sucesso", {
        id: 'generate-nfse',
        description: !invoice.profile?.tiny_customer_id ? 'Cliente sincronizado automaticamente' : undefined
      });
      await loadInvoices(); // Reload to get updated nfse data
    } catch (error: any) {
      console.error("Erro ao gerar NFS-e:", error);
      toast.error(error.message || "Erro ao gerar NFS-e");
    } finally {
      setGeneratingNfse(null);
    }
  };

  const handleDownloadNfsePdf = async (invoice: InvoiceWithProfile) => {
    try {
      setDownloadingPdf(invoice.id);

      toast.loading('Baixando PDF da NFS-e...', { id: 'download-nfse' });

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: {
          action: 'download-nfse-pdf',
          invoiceId: invoice.id
        }
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao baixar PDF');
      }

      toast.success("PDF da NFS-e baixado com sucesso", { id: 'download-nfse' });

      // Recarregar invoices para pegar o nfse_pdf_url atualizado
      await loadInvoices();

      // Abrir o PDF em nova aba
      if (data?.data?.nfse_pdf_url) {
        window.open(data.data.nfse_pdf_url, '_blank');
      }

    } catch (error: any) {
      console.error("Erro ao baixar PDF da NFS-e:", error);
      toast.error(error.message || "Erro ao baixar PDF da NFS-e", { id: 'download-nfse' });
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleRefreshNfseData = async (invoiceId: string) => {
    toast.loading('Buscando dados da NFS-e...', { id: 'refresh-nfse' });

    try {
      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: {
          action: 'refresh-nfse-data',
          invoiceId
        }
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao atualizar dados');
      }

      toast.success('Dados da NFS-e atualizados!', {
        id: 'refresh-nfse',
        description: data.pdf_downloaded ? 'PDF baixado automaticamente' : 'Link da prefeitura disponível'
      });

      await loadInvoices();
    } catch (error: any) {
      console.error('Erro ao atualizar dados da NFS-e:', error);
      toast.error(error.message || 'Erro ao atualizar dados da NFS-e', { id: 'refresh-nfse' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <div className="grid gap-4 md:grid-cols-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Pagamentos</h1>
        <p className="text-muted-foreground">
          Gerenciar histórico de pagamentos e NFS-e
        </p>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Arrecadado</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(totalArrecadado)}</div>
            <p className="text-xs text-muted-foreground">
              Pagamentos confirmados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Pagamentos</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPagamentos}</div>
            <p className="text-xs text-muted-foreground">
              Todas as transações
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{taxaSucesso}%</div>
            <p className="text-xs text-muted-foreground">
              Pagamentos bem-sucedidos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela de Pagamentos */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Pagamentos</CardTitle>
          <CardDescription>
            {filteredInvoices.length} pagamento{filteredInvoices.length !== 1 ? 's' : ''} encontrado{filteredInvoices.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, CPF ou NFS-e..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {filteredInvoices.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="Nenhum pagamento encontrado"
              description={searchTerm ? "Tente ajustar sua busca" : "Ainda não há pagamentos registrados"}
            />
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Profissional</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead>Tiny ERP</TableHead>
                    <TableHead>NFS-e</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{invoice.profile?.full_name || 'N/A'}</div>
                          <div className="text-sm text-muted-foreground">{invoice.profile?.health_area || ''}</div>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        {format(new Date(invoice.paid_at || invoice.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatPrice(invoice.amount_cents)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {invoice.payment_method || 'Cartão de crédito'}
                      </TableCell>
                      <TableCell>
                        {invoice.profile?.tiny_customer_id ? (
                          <Badge variant="secondary" className="gap-1">
                            <Users className="h-3 w-3" />
                            Sincronizado
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {invoice.nfse_number ? (
                          <Badge variant="outline">{invoice.nfse_number}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewInvoice(invoice)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>

                          {invoice.status === 'paid' && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSyncCustomer(invoice)}
                                disabled={syncingCustomer === invoice.id}
                                title="Sincronizar cliente no Tiny ERP"
                              >
                                {syncingCustomer === invoice.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Users className="w-4 h-4" />
                                )}
                              </Button>

                              {!invoice.tiny_nfse_id && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleGenerateNfse(invoice)}
                                  disabled={generatingNfse === invoice.id}
                                  title="Gerar NFS-e (sincroniza cliente automaticamente se necessário)"
                                >
                                  {generatingNfse === invoice.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <FileText className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </>
                          )}

                          {/* Botão de Atualizar dados da NFS-e (para invoices antigas) */}
                          {invoice.status === 'paid' && invoice.tiny_nfse_id && !invoice.nfse_verification_code && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRefreshNfseData(invoice.id)}
                              title="Atualizar dados da NFS-e"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Botão de download do PDF da NFS-e */}
                          {invoice.status === 'paid' && invoice.tiny_nfse_id && (
                            <>
                              {invoice.nfse_pdf_url ? (
                                // PDF já disponível - download direto
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  asChild
                                  title="Baixar NFS-e"
                                >
                                  <a href={invoice.nfse_pdf_url} download target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4" />
                                  </a>
                                </Button>
                              ) : invoice.nfse_url ? (
                                // Link para o portal da prefeitura disponível
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  asChild
                                  title="Ver NFS-e no portal da prefeitura"
                                >
                                  <a href={invoice.nfse_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                </Button>
                              ) : (
                                // PDF não disponível - tentar baixar
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDownloadNfsePdf(invoice)}
                                  disabled={downloadingPdf === invoice.id}
                                  title="Baixar PDF da NFS-e"
                                >
                                  {downloadingPdf === invoice.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Detalhes */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Pagamento</DialogTitle>
            <DialogDescription>
              Informações completas sobre a transação
            </DialogDescription>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Profissional</p>
                  <p className="text-sm">{selectedInvoice.profile?.full_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">CPF</p>
                  <p className="text-sm">{selectedInvoice.profile?.cpf || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Área</p>
                  <p className="text-sm">{selectedInvoice.profile?.health_area || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <div>{getStatusBadge(selectedInvoice.status)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Valor</p>
                  <p className="text-sm font-bold">{formatPrice(selectedInvoice.amount_cents)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Método de Pagamento</p>
                  <p className="text-sm">{selectedInvoice.payment_method || 'Cartão de crédito'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Data de Pagamento</p>
                  <p className="text-sm">
                    {selectedInvoice.paid_at
                      ? format(new Date(selectedInvoice.paid_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                      : '-'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Data de Criação</p>
                  <p className="text-sm">
                    {format(new Date(selectedInvoice.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">ID Pagar.me (Charge)</p>
                  <p className="text-sm font-mono text-xs">{selectedInvoice.pagarme_charge_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">ID Pagar.me (Invoice)</p>
                  <p className="text-sm font-mono text-xs">{selectedInvoice.pagarme_invoice_id || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Número NFS-e</p>
                  <p className="text-sm">{selectedInvoice.nfse_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">NFS-e</p>
                  {selectedInvoice.nfse_pdf_url ? (
                    <Button size="sm" variant="outline" asChild>
                      <a href={selectedInvoice.nfse_pdf_url} download target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-2" />
                        Baixar PDF
                      </a>
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">Não emitida</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Pagamentos;
